// @spec GHG-STR-043
/**
 * Visual indicator for AI classification confidence levels
 */
'use client';

import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

import type { ConfidenceLevel } from '../types/classification';
import { getClassificationConfidenceLevel } from '../types/classification';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
interface ConfidenceBadgeProps {
  confidence: number;
  showPercentage?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Get badge color classes for confidence level
 */
function getConfidenceColorClasses(level: ConfidenceLevel): string {
  switch (level) {
    case 'high':
      return 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-300';
    case 'medium':
      return 'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-300';
    case 'low':
      return 'bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300';
  }
}

/**
 * Get icon for confidence level
 */
function ConfidenceIcon({ level, className }: { level: ConfidenceLevel; className?: string }) {
  switch (level) {
    case 'high':
      return <CheckCircle2 className={cn('h-3 w-3', className)} />;
    case 'medium':
      return <Info className={cn('h-3 w-3', className)} />;
    case 'low':
      return <AlertTriangle className={cn('h-3 w-3', className)} />;
    default:
      return null;
  }
}

/**
 * Get size classes
 */
function getSizeClasses(size: 'sm' | 'md'): string {
  switch (size) {
    case 'sm':
      return 'text-xs px-1.5 py-0 gap-1';
    default:
      return 'text-xs px-2 py-0.5 gap-1.5';
  }
}

/**
 * Get confidence level description for tooltip
 */
function getLevelDescription(level: ConfidenceLevel): string {
  switch (level) {
    case 'high':
      return 'High confidence - AI is confident in this classification';
    case 'medium':
      return 'Medium confidence - Review recommended';
    case 'low':
      return 'Low confidence - Manual verification required';
    default:
      return 'Unknown confidence level';
  }
}

/**
 * Format confidence as percentage
 */
function formatConfidence(confidence: number): string {
  return `${String(Math.round(confidence * 100))}%`;
}

export function ConfidenceBadge({
  confidence,
  showPercentage = false,
  size = 'md',
}: ConfidenceBadgeProps) {
  const level = getClassificationConfidenceLevel(confidence);

  const displayText = showPercentage
    ? formatConfidence(confidence)
    : level.charAt(0).toUpperCase() + level.slice(1);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className={cn(
              getConfidenceColorClasses(level),
              getSizeClasses(size),
              'cursor-default inline-flex items-center'
            )}
          >
            <ConfidenceIcon level={level} />
            {displayText}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{level.charAt(0).toUpperCase() + level.slice(1)} Confidence</p>
          <p className="text-xs text-muted-foreground">{getLevelDescription(level)}</p>
          <p className="mt-1 text-xs">Score: {formatConfidence(confidence)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Get the highlight class for low confidence fields
 */
export function getConfidenceHighlightClass(confidence: number): string {
  const level = getClassificationConfidenceLevel(confidence);

  switch (level) {
    case 'low':
      return 'ring-2 ring-red-200 dark:ring-red-800';
    case 'medium':
      return 'ring-1 ring-amber-200 dark:ring-amber-800';
    default:
      return '';
  }
}
